import { useNavigate } from "react-router-dom";

type ErrorCode = 404 | 403 | 500 | number;

interface ErrorPageProps {
    code?: ErrorCode;
    title?: string;
    message?: string;
    /** Teks tombol kembali (default: "Kembali ke Beranda") */
    actionLabel?: string;
    /** Override fungsi tombol (default: navigate ke "/") */
    onAction?: () => void;
}

const presets: Record<
    number,
    { emoji: string; title: string; message: string }
> = {
    404: {
        emoji: "◈",
        title: "Halaman Tidak Ditemukan",
        message:
            "Halaman yang kamu cari tidak ada atau sudah dipindahkan. Periksa kembali URL-nya, atau kembali ke beranda.",
    },
    403: {
        emoji: "◎",
        title: "Akses Ditolak",
        message:
            "Kamu tidak memiliki izin untuk mengakses halaman ini. Silakan masuk dengan akun yang sesuai.",
    },
    500: {
        emoji: "◉",
        title: "Terjadi Kesalahan",
        message:
            "Server mengalami masalah sementara. Tim kami sedang menanganinya. Coba beberapa saat lagi.",
    },
};

export default function ErrorPage({
    code = 404,
    title,
    message,
    actionLabel = "Kembali ke Beranda",
    onAction,
}: ErrorPageProps) {
    const navigate = useNavigate();

    const preset = presets[code] ?? {
        emoji: "◈",
        title: "Terjadi Kesalahan",
        message: "Sesuatu yang tidak terduga terjadi. Silakan coba lagi.",
    };

    const displayTitle = title ?? preset.title;
    const displayMessage = message ?? preset.message;

    const handleAction = () => {
        if (onAction) {
            onAction();
        } else {
            navigate("/");
        }
    };

    return (
        <div
            className="fixed inset-0 z-50 w-full flex items-center justify-center
                       overflow-hidden bg-cream dark:bg-dark px-6 transition-colors duration-300">
            {/* Decorative blurred blobs */}
            <span
                aria-hidden
                className="pointer-events-none absolute top-1/4 left-1/2 -translate-x-1/2
                           w-120 h-120 rounded-full
                           bg-gold/8 dark:bg-gold/5 blur-[120px]"
            />

            <div className="relative z-10 flex flex-col items-center text-center max-w-lg w-full">
                {/* Floating glyph badge */}
                <div
                    className="relative mb-6 sm:mb-10 select-none"
                    style={{ animation: "errorFloat 4s ease-in-out infinite" }}>
                    {/* Outer ring */}
                    <span
                        className="absolute inset-0 rounded-full border border-gold/20
                                   dark:border-gold/15"
                        style={{
                            animation: "errorPulseRing 3s ease-in-out infinite",
                        }}
                    />
                    {/* Card */}
                    <div
                        className="relative w-24 h-24 sm:w-36 sm:h-36 rounded-full
                                   bg-white dark:bg-dark-2
                                   border border-gold/20 dark:border-white/8
                                   shadow-xl dark:shadow-black/40
                                   flex flex-col items-center justify-center gap-1">
                        {/* Glyph */}
                        <span className="text-3xl sm:text-4xl text-gold dark:text-gold-light leading-none">
                            {preset.emoji}
                        </span>
                        {/* Error code */}
                        <span
                            className="font-serif font-bold text-2xl sm:text-3xl tracking-tight
                                       text-dark dark:text-white/90 leading-none">
                            {code}
                        </span>
                    </div>
                </div>

                {/* Divider dot row */}
                <div className="flex items-center gap-2 mb-4 sm:mb-7">
                    <span className="w-8 h-px bg-gold/30 dark:bg-gold/20" />
                    <span className="w-1.5 h-1.5 rounded-full bg-gold/50" />
                    <span className="w-8 h-px bg-gold/30 dark:bg-gold/20" />
                </div>

                {/* Title */}
                <h1
                    className="font-serif text-2xl sm:text-4xl font-bold
                               text-dark dark:text-white leading-tight mb-3 sm:mb-4">
                    {displayTitle}
                </h1>

                {/* Message */}
                <p
                    className="font-sans text-xs sm:text-base leading-relaxed
                               text-dark/55 dark:text-white/45 mb-6 sm:mb-10 max-w-sm">
                    {displayMessage}
                </p>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    {/* Primary CTA */}
                    <button
                        onClick={handleAction}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2
                                   px-7 py-3 rounded-xl font-sans font-semibold text-sm
                                   bg-gold hover:bg-gold-light text-white
                                   shadow-md hover:shadow-gold/30 dark:hover:shadow-gold/20
                                   transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0
                                   cursor-pointer">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                            <polyline points="9 22 9 12 15 12 15 22" />
                        </svg>
                        {actionLabel}
                    </button>

                    {/* Secondary — go back */}
                    <button
                        onClick={() => navigate(-1)}
                        className="w-full sm:w-auto inline-flex items-center justify-center gap-2
                                   px-7 py-3 rounded-xl font-sans font-semibold text-sm
                                   bg-transparent border border-gold/30 dark:border-white/10
                                   text-dark/70 dark:text-white/60
                                   hover:border-gold/60 dark:hover:border-white/25
                                   hover:text-dark dark:hover:text-white
                                   transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0
                                   cursor-pointer">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round">
                            <path d="m15 18-6-6 6-6" />
                        </svg>
                        Kembali
                    </button>
                </div>

                {/* Wordmark footer */}
                <p className="mt-8 sm:mt-14 font-serif text-sm tracking-[0.25em] text-dark/25 dark:text-white/20 select-none">
                    OMAH
                    <em className="not-italic text-gold/60 dark:text-gold/40">
                        ·
                    </em>
                    RING
                </p>
            </div>
        </div>
    );
}
